import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { signOutUser } from '../../utils/auth';

const IconGenerate = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

const IconDocuments = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const IconTracker = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
  </svg>
);

const IconPrep = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const IconSignOut = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const SidebarNav = ({ onNewGeneration }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleSignOut = () => signOutUser({ dispatch, navigate, userId: user?.id });

  const handleNewGeneration = () => {
    if (onNewGeneration) {
      onNewGeneration();
    } else {
      navigate('/create');
    }
  };

  const navItems = [
    {
      id: 'generate',
      label: 'New Generation',
      Icon: IconGenerate,
      onClick: handleNewGeneration,
      active: location.pathname === '/create',
    },
    {
      id: 'documents',
      label: 'My Documents',
      Icon: IconDocuments,
      onClick: () => navigate('/dashboard'),
      active: location.pathname === '/dashboard',
    },
    {
      id: 'tracker',
      label: 'Job Tracker — Coming Soon',
      Icon: IconTracker,
      onClick: null,
      soon: true,
    },
    {
      id: 'prep',
      label: 'Interview Prep — Coming Soon',
      Icon: IconPrep,
      onClick: null,
      soon: true,
    },
  ];

  const initials = (user?.email?.[0] ?? '?').toUpperCase();

  return (
    <nav className="sidebar-nav">
      {/* Z Logo */}
      <button className="sidebar-logo" onClick={handleNewGeneration} title="ZYNACULAR">
        <span className="sidebar-logo-mark">Z</span>
      </button>

      {/* Nav Items */}
      <div className="sidebar-items">
        {navItems.map(({ id, label, Icon, onClick, active, soon }) => (
          <div key={id} className="sidebar-item-wrapper">
            <button
              className={`sidebar-item ${active ? 'sidebar-item--active' : ''} ${soon ? 'sidebar-item--soon' : ''}`}
              onClick={onClick || undefined}
              aria-label={label}
              disabled={!onClick}
            >
              <Icon />
            </button>
            <span className="sidebar-tooltip">{label}</span>
          </div>
        ))}
      </div>

      {/* User Avatar at bottom */}
      <div className="sidebar-bottom">
        <div className="sidebar-item-wrapper">
          <button
            className="sidebar-user-btn"
            onClick={() => setShowUserMenu(v => !v)}
            aria-label="User menu"
            title={user?.email || 'Account'}
          >
            <span className="sidebar-user-initials">{initials}</span>
          </button>
          {showUserMenu && (
            <div className="sidebar-user-menu">
              <div className="sidebar-user-email">{user?.email}</div>
              <button className="sidebar-sign-out-btn" onClick={handleSignOut}>
                <IconSignOut />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default SidebarNav;
